<template>
  <div class="flex flex-col px-2 md:px-0 h-full pt-2 md:pt-0">
    <tabs-wrapper contentClass="md:px-8 md:pt-4">
      <template #header-end-0>
        <Button label="הוספת לקוח/ה" icon="pi pi-user-plus" size="small" @click="addCustomer" />
      </template>
      <tab title="לקוחות" :count="totalCustomers">
       <div class="relative flex flex-col w-full h-full min-h-0">
        <div ref="scrollContainer" class="flex flex-col max-h-full h-full w-full overflow-y-scroll pt-2" @scroll="onScroll">
          <!-- Initial loading -->
          <div class="flex flex-col px-2 w-full h-[90vh]"
               v-if="!initialLoaded"
               style="display: flex; justify-content: center; align-items: center">
            <loader/>
          </div>
          <!-- Has customers (or had them before searching) -->
          <div v-else-if="hasCustomers || searchValue || showExample" class="flex flex-col gap-4 w-full h-full">
            <!-- Add / import moved to the header "+" action (see onMounted) -->

            <!-- Example customer preview, so a new owner can see a populated card -->
            <div v-if="showExample" class="w-full max-w-[440px] mb-8 px-2">
              <div class="flex items-center gap-2 mb-2 text-primary-600">
                <i class="pi pi-eye" />
                <span class="text-sm font-medium">ככה ייראה כרטיס לקוח — לחצו להצצה</span>
              </div>
              <customer-row-card
                :label="getTwoChars('דנה לוי')"
                title="דנה לוי"
                subtitle="050-123-4567"
                tag="לדוגמה"
                :titleIcons="['pi-verified']"
                @click="openExampleCustomer"
              />
            </div>

            <div v-else class="flex flex-col">
              <div class="w-full justify-center items-center mb-4 md:max-w-[300px]">
                <IconField>
                  <InputIcon class="pi pi-search" />
                  <InputText size="small" v-model="searchValue" placeholder="חיפוש" class="w-full" />
                </IconField>
              </div>
              <!-- Search loading indicator -->
              <div v-if="isSearching" class="flex justify-center items-center py-8">
                <loader />
              </div>
              <!-- Customer list -->
              <template v-else-if="clients?.length > 0">
                <transition-group
                  mode="out-in"
                  name="list"
                  class="w-full h-full flex flex-col flex-grow md:flex-row md:flex-wrap md:gap-6"
                  tag="div"
                >
                  <customer-row-card
                    v-for="client of clients"
                    :key="String(client.id)"
                    :label="getTwoChars(client.fullName)"
                    :title="client.fullName"
                    :subtitle="formatPhone(client.phoneNumber)"
                    :tag="client.tags?.length > 0 ? client.tags[0] : null"
                    :titleIcons="calcTitleIcons(client)"
                    @click="goToClient(client.id)"
                  />
                </transition-group>

                <!-- Infinite scroll: the sentinel triggers the next page as it comes into view -->
                <div v-if="hasMore" ref="loadMoreSentinel" class="w-full h-px flex-none" />
                <div v-if="isLoadingMore" class="flex justify-center items-center py-6">
                  <loader />
                </div>
                <div
                  v-else-if="!hasMore && clients.length > pageSize"
                  class="text-center text-xs text-surface-400 py-6"
                >אלה כל הלקוחות</div>
              </template>
              <!-- No search results -->
              <div v-else class="flex flex-col items-center justify-center py-8 text-surface-500">
                <i class="pi pi-search text-3xl mb-3" />
                <span class="text-lg font-medium">לא נמצאו לקוחות</span>
                <span class="text-sm">נסו לחפש עם מילות מפתח אחרות</span>
              </div>
            </div>
          </div>
          <!-- No customers at all (empty state) -->
          <div v-else class="w-full h-max flex flex-col justify-center items-center my-auto">
            <img
              src="../../../assets/img/admin/no-clients.svg"
              class="w-32 mb-6"
              alt="אין לקוחות">
            <span class="text-lg font-medium">קצת שומם פה כרגע</span>
            <span
              class="text-center px-4 font-light">הלקוחות שלך יופיעו פה באופן אוטומטי לאחר שהם יזמינו תור דרך האתר. כמובן שאפשר גם לייבא לקוחות או ליצור לקוחות חדשים בקלות. </span>
            <div class="flex justify-around">
              <div
                class="relative py-2 px-4 border border-solid m-2 bg-primary-500 text-white rounded-3xl border-purple-800 flex gap-2 justify-center items-center mt-4"
                @click="addCustomer">
                <i class="pi pi-user-plus"/>
                <span class="text-sm">יצירת לקוח</span>
              </div>
              <div
                class="relative py-2 px-4 border border-solid m-2 bg-white text-purple-500 rounded-3xl border-purple-800 flex gap-2 justify-center items-center mt-4"
                @click="importCustomers">
                <i class="pi pi-users"/>
                <span class="text-sm">יבוא לקוחות מאנשי הקשר</span>
              </div>
            </div>
          </div>
          <ConfirmDialog/>
        </div>

        <!-- Shown once infinite scroll has pulled in at least one extra page -->
        <transition name="back-to-top">
          <button
            v-if="showBackToTop"
            class="absolute bottom-4 end-4 z-20 w-11 h-11 rounded-full bg-white shadow-lg border border-gray-200 flex items-center justify-center text-primary-600"
            aria-label="חזרה למעלה"
            @click="scrollToTop"
          >
            <i class="pi pi-arrow-up" />
          </button>
        </transition>
       </div>
      </tab>
      <tab title="משובים" :count="reviewsCount" description="דירוגים ומשובים שהלקוחות השאירו לאחר ביקור">
        <reviews-list @total-changed="reviewsCount = $event"/>
      </tab>
    </tabs-wrapper>
  </div>
</template>

<script lang="ts" setup>
import {type ICustomer, ContractStatus} from '@torim-app/types';
import ConfirmDialog from 'primevue/confirmdialog';

import {computed, nextTick, onBeforeUnmount, onMounted, ref, watch} from 'vue';
import {useRouter} from 'vue-router';
import {useAdminClientsStore, useAdminStore} from '@app/stores';

import {AdminRouteNames} from '@app/router/route-names';
import {getTwoChars} from "@torim-app/utils";
import NewCustomer from '@app/components/admin/modals/NewCustomer.vue';
import ImportClients from '@app/components/admin/modals/ImportClients.vue';
import { useDrawer } from '@torim-app/shared-components';
import CustomerRowCard from "@app/components/admin/pages/customer/CustomerRowCard.vue";
import ExampleCustomerProfile from "@app/components/admin/modals/ExampleCustomerProfile.vue";
import ReviewsList from "@app/components/admin/pages/customer/ReviewsList.vue";
import Tab from './components/admin-tabs/Tab.vue'
import InputIcon from 'primevue/inputicon';
import IconField from 'primevue/iconfield';
import { useHeaderAction } from '@app/composables/header-action';
import { trackEvent } from '@app/composables/posthog';

const adminClientsStore = useAdminClientsStore();
const adminStore = useAdminStore();
const router = useRouter();
const { openDrawer, closeDrawer } = useDrawer();
const { setHeaderAction } = useHeaderAction();

const searchTimeout = ref<any>();

const {setPage} = adminStore;

// The list (items, search, how much was loaded, scroll position) lives in the store so
// that opening a customer and coming back restores the page exactly as it was left.
const customersList = computed(() => adminClientsStore.customersList);

const searchValue = ref(adminClientsStore.customersList.search);
const initialLoaded = ref(false);
const isSearching = ref(false);
const isLoadingMore = ref(false);
const pageSize = ref(20);

const scrollContainer = ref<HTMLElement>();
const loadMoreSentinel = ref<HTMLElement>();
const scrollTop = ref(adminClientsStore.customersList.scrollTop);
let sentinelObserver: IntersectionObserver | undefined;
let scrollFrame = 0;

const totalCustomers = computed(() => customersList.value.total);
const hasMore = computed(() => adminClientsStore.hasMoreCustomersInList);
// "Back to top" appears only after infinite scroll actually pulled in another page,
// and only once the user is far enough down for it to be of any use.
const showBackToTop = computed(() => customersList.value.extraPagesLoaded > 0 && scrollTop.value > 400);

// Total reviews, reported up by ReviewsList so the "משובים" tab can show the count.
const reviewsCount = ref(0);

const clients = computed(() => customersList.value.items);
const hasCustomers = computed(() => totalCustomers.value > 0 || clients.value?.length > 0);
const isMainClient = computed(() => adminStore.getMainClient);
const canEdit = computed(() => adminStore.getCanEdit);
const isInTrial = computed(() => adminStore.getBusinessSiteData?.contract?.status === ContractStatus.Trial);
// Only for a genuinely new business on the trial plan that has no real customers
// yet (and isn't mid-search). Established/paying businesses never see the example.
const showExample = computed(() =>
  isInTrial.value &&
  !hasCustomers.value &&
  !searchValue.value
);

const formatPhone = (phoneNumber: string) => {
  return `${phoneNumber.substring(0, 3)}-${phoneNumber.substring(3, 6)}-${phoneNumber.substring(6, 10)}`;
};

watch(searchValue, (newVal) => {
  if (searchTimeout.value) {
    clearTimeout(searchTimeout.value);
  }

  searchTimeout.value = setTimeout(() => {
    if (newVal) trackEvent('clients_searched');
    loadCustomers();
  }, 500);
});

const loadCustomers = async () => {
  isSearching.value = true;
  try {
    await adminClientsStore.fetchCustomersListFirstPage(pageSize.value, searchValue.value);
    scrollTop.value = 0;
    if (scrollContainer.value) scrollContainer.value.scrollTop = 0;
  } finally {
    initialLoaded.value = true;
    isSearching.value = false;
  }
  await nextTick();
  observeSentinel();
};

const loadMore = async () => {
  if (isLoadingMore.value || isSearching.value || !hasMore.value) return;

  isLoadingMore.value = true;
  try {
    await adminClientsStore.fetchCustomersListNextPage(pageSize.value);
    trackEvent('clients_list_loaded_more', { page: customersList.value.extraPagesLoaded });
  } catch (e) {
    console.error(e);
  } finally {
    isLoadingMore.value = false;
  }

  await nextTick();
  // While the list is still shorter than the viewport no scroll event can fire, so
  // re-observing is what keeps pulling pages until the container actually scrolls.
  if (hasMore.value && !isScrollable()) observeSentinel();
};

const isScrollable = () => {
  const el = scrollContainer.value;
  return !!el && el.scrollHeight > el.clientHeight + 50;
};

const observeSentinel = () => {
  sentinelObserver?.disconnect();
  if (!loadMoreSentinel.value) return;

  sentinelObserver = new IntersectionObserver(entries => {
    if (entries.some(entry => entry.isIntersecting)) loadMore();
  }, { root: scrollContainer.value ?? null, rootMargin: '300px' });
  sentinelObserver.observe(loadMoreSentinel.value);
};

// The sentinel is only in the DOM while there are more customers to load — re-attach the
// observer whenever it appears (first render, after a search, when it comes back).
watch(loadMoreSentinel, () => observeSentinel());

// Adding / importing a customer drops the cache while this page is still on screen
// (the drawer renders on top of it) — reload so the list never shows up empty.
watch(() => customersList.value.isLoaded, (isLoaded) => {
  if (!isLoaded && initialLoaded.value) loadCustomers().catch(e => console.error(e));
});

const onScroll = () => {
  if (scrollFrame) return;
  scrollFrame = requestAnimationFrame(() => {
    scrollFrame = 0;
    const el = scrollContainer.value;
    if (!el) return;

    scrollTop.value = el.scrollTop;
    if (hasMore.value && el.scrollHeight - el.scrollTop - el.clientHeight < 300) loadMore();
  });
};

const scrollToTop = () => {
  trackEvent('clients_back_to_top_clicked');
  scrollContainer.value?.scrollTo({ top: 0, behavior: 'smooth' });
};

const restoreScroll = async () => {
  const saved = customersList.value.scrollTop;
  if (!saved) return;

  await nextTick();
  requestAnimationFrame(() => {
    if (scrollContainer.value) {
      scrollContainer.value.scrollTop = saved;
      scrollTop.value = saved;
    }
  });
};

const goToClient = (clientId: string): void => {
  trackEvent('customer_profile_clicked');
  router.push({
    name: AdminRouteNames.ClientProfile,
    params: {id: clientId},
  });
};

const hideSidebar = () => {
  closeDrawer();
  adminStore.fetchBusinessData().then((_) => console.log('Loaded'));
}

const calcTitleIcons = (client: Pick<ICustomer, 'id' | 'fullName' | 'tags' | 'loyalty'>) => {
  const res = [];
  if (client.loyalty) res.push('pi-verified')
  // if (client.loyalty?.birthday && DateTime.fromISO(client.loyalty.birthday as string).month === DateTime.local().month) res.push('pi-gift')
  return res;
}

trackEvent('clients_viewed');
setPage(AdminRouteNames.Clients);

const importCustomers = () => {
  trackEvent('import_customers_clicked');
  openDrawer(ImportClients, { onClose: hideSidebar }, { title: 'יבוא לקוחות',  noPadding: true });
};

// From the "add customer" drawer: close it, then open the import drawer once the close
// animation finishes (openDrawer would otherwise stack a second drawer on top).
const importFromNewCustomer = () => {
  closeDrawer();
  setTimeout(() => importCustomers(), 320);
};

const addCustomer = () => {
  trackEvent('add_customer_clicked');
  openDrawer(
    NewCustomer,
    { onClose: hideSidebar, onImport: importFromNewCustomer, showImport: isMainClient.value },
    { title: 'הוספת לקוח חדש' }
  );
};

onMounted(async () => {
  // Add / import customers now lives in the header "+" action (the drawer offers import).
  if (canEdit.value) {
    setHeaderAction({ icon: 'pi-user-plus', onClick: addCustomer, ariaLabel: 'הוספת לקוח/ה' });
  }

  // Coming back from a customer profile: reuse what was already loaded and put the user
  // back where they were. A mutation (add / import / delete) drops the cache first.
  if (customersList.value.isLoaded) {
    initialLoaded.value = true;
    await restoreScroll();
    observeSentinel();
    return;
  }

  loadCustomers().catch(e => console.error(e));
});

onBeforeUnmount(() => {
  if (scrollFrame) cancelAnimationFrame(scrollFrame);
  sentinelObserver?.disconnect();
  adminClientsStore.setCustomersListScrollTop(scrollContainer.value?.scrollTop ?? scrollTop.value);
});

const openExampleCustomer = () => {
  trackEvent('example_customer_opened');
  openDrawer(ExampleCustomerProfile, {}, {
    title: 'כרטיס לקוח לדוגמה',
    fullMobile: true,
    full: true,
    noPadding: true
  });
}
</script>

<style lang="scss" scoped>
@use '../../../assets/css/vars' as *;
@use '../../../assets/css/transitions' as *;

.all-clients {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  flex-grow: 1;

  .p-inputtext,
  .p-input-icon-right {
    width: 100%;
  }

  .contacts {
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;
    flex-grow: 1;
    //overflow-y: auto;
    padding: 1rem 0;

    .contact {
      display: flex;
      width: 100%;
      padding: 0.5rem 0.25rem;
      flex-direction: row;
      justify-content: flex-start;
      align-items: center;

      &:hover {
        background-color: $gray-bg;
      }

      .texts {
        display: flex;
        height: 3rem;
        flex-grow: 1;
        flex-direction: column;
        margin-right: 1rem;
        align-items: flex-start;
        justify-content: space-evenly;
      }

      .p-button {
        background: transparent;
        border: none;
        color: black;

        &:hover {
          color: grey;
        }
      }
    }
  }
}

.back-to-top-enter-active,
.back-to-top-leave-active {
  transition: opacity 0.2s ease, transform 0.2s ease;
}

.back-to-top-enter-from,
.back-to-top-leave-to {
  opacity: 0;
  transform: translateY(0.5rem);
}

.no-clients {
  display: flex;
  width: 100%;
  height: 100%;
  justify-content: center;
  align-items: center;
  flex-direction: column;

  span {
    margin-bottom: 0.3rem;
    text-align: center;
    padding: 0 1.5rem;
  }
}
</style>
